import {
  Box,
  DialogActions,
  Grid,
  Button,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-github";
import "ace-builds/src-min-noconflict/ext-language_tools";
import "ace-builds/src-min-noconflict/ext-searchbox";
import { useState, useEffect, useCallback } from "react";
import EnhancedQueryDialog from "./EnhancedQueryDialog";
import { useToast } from "../../services/utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "./ViewPayloadDialog.scss";

interface ValidationErrorItem {
  field?: string;
  error?: string;
  type?: string;
}

interface ValidationResult {
  is_valid: boolean;
  errors?: (string | ValidationErrorItem)[];
  warnings?: (string | ValidationErrorItem)[];
}

type PayloadSection = "resource" | "resource_column_details";

interface ViewPayloadDialogProps {
  openDialog: boolean;
  dialogTitle?: string;
  payload: any;
  handleDialogClose?: () => void;
  onValidate?: (payload: any, section?: PayloadSection) => Promise<any>;
  isValidating?: boolean;
  validationResponse?: ValidationResult | null;
}

const ViewPayloadDialog = ({
  openDialog,
  dialogTitle = "Validation JSON",
  payload,
  handleDialogClose,
  onValidate,
  isValidating = false,
  validationResponse = null,
}: ViewPayloadDialogProps) => {
  const { showToast } = useToast();
  const [isCopying, setIsCopying] = useState(false);

  // Local state for the edited JSON string — does NOT affect the parent's payload
  const [editedPayload, setEditedPayload] = useState("{}");
  const [isModified, setIsModified] = useState(false);
  // Store the original payload when the dialog opens to persist user changes across validate/reset
  const [originalPayload, setOriginalPayload] = useState("{}");

  // Split-payload mode: when the payload contains both `resource` and
  // `resource_column_details` keys (Add/Edit Resource flows), render two
  // independent editors inside an Accordion instead of a single editor.
  const [isSplitMode, setIsSplitMode] = useState(false);

  const [editedResourceJson, setEditedResourceJson] = useState("{}");
  const [originalResourceJson, setOriginalResourceJson] = useState("{}");
  const [isModifiedResource, setIsModifiedResource] = useState(false);

  const [editedResourceColumnDetailsJson, setEditedResourceColumnDetailsJson] =
    useState("{}");
  const [
    originalResourceColumnDetailsJson,
    setOriginalResourceColumnDetailsJson,
  ] = useState("{}");
  const [isModifiedResourceColumnDetails, setIsModifiedResourceColumnDetails] =
    useState(false);

  const [resourceExpanded, setResourceExpanded] = useState(true);
  const [resourceColumnDetailsExpanded, setResourceColumnDetailsExpanded] =
    useState(false);

  // Tracks which accordion's Validate button was clicked, so only that
  // button shows the "Validating..." state (isValidating is a single shared
  // flag from the parent, covering both sections).
  const [activeValidateSection, setActiveValidateSection] =
    useState<PayloadSection | null>(null);

  // Sync local state only when the dialog opens (not on every payload change)
  // This preserves user edits when validate is clicked
  useEffect(() => {
    if (openDialog) {
      const isSplit = Boolean(
        payload &&
        typeof payload === "object" &&
        !Array.isArray(payload) &&
        "resource" in payload &&
        "resource_column_details" in payload,
      );
      setIsSplitMode(isSplit);

      if (isSplit) {
        const resourceFormatted = JSON.stringify(
          payload.resource ?? {},
          null,
          2,
        );
        const resourceColumnDetailsFormatted = JSON.stringify(
          payload.resource_column_details ?? {},
          null,
          2,
        );
        setEditedResourceJson(resourceFormatted);
        setOriginalResourceJson(resourceFormatted);
        setIsModifiedResource(false);
        setEditedResourceColumnDetailsJson(resourceColumnDetailsFormatted);
        setOriginalResourceColumnDetailsJson(resourceColumnDetailsFormatted);
        setIsModifiedResourceColumnDetails(false);
        setResourceExpanded(true);
        setResourceColumnDetailsExpanded(false);
      } else {
        const formatted = payload ? JSON.stringify(payload, null, 2) : "{}";
        setEditedPayload(formatted);
        setOriginalPayload(formatted);
        setIsModified(false);
      }
    }
  }, [openDialog]);

  const handleEditorChange = useCallback(
    (newValue: string) => {
      setEditedPayload(newValue);
      setIsModified(newValue !== originalPayload);
    },
    [originalPayload],
  );

  const handleResourceEditorChange = useCallback(
    (newValue: string) => {
      setEditedResourceJson(newValue);
      setIsModifiedResource(newValue !== originalResourceJson);
    },
    [originalResourceJson],
  );

  const handleResourceColumnDetailsEditorChange = useCallback(
    (newValue: string) => {
      setEditedResourceColumnDetailsJson(newValue);
      setIsModifiedResourceColumnDetails(
        newValue !== originalResourceColumnDetailsJson,
      );
    },
    [originalResourceColumnDetailsJson],
  );

  const combinedIsModified = isSplitMode
    ? isModifiedResource || isModifiedResourceColumnDetails
    : isModified;

  const handleResetJson = () => {
    if (isSplitMode) {
      setEditedResourceJson(originalResourceJson);
      setIsModifiedResource(false);
      setEditedResourceColumnDetailsJson(originalResourceColumnDetailsJson);
      setIsModifiedResourceColumnDetails(false);
    } else {
      setEditedPayload(originalPayload);
      setIsModified(false);
    }
  };

  const handleCopyJson = async (section?: PayloadSection) => {
    try {
      setIsCopying(true);
      let textToCopy = editedPayload;
      if (isSplitMode) {
        try {
          // When a specific section is provided, copy only that section.
          // This allows users to copy the resource or resource_column_details
          // payload independently, matching the actual API contract.
          if (section === "resource") {
            const parsedResource = JSON.parse(editedResourceJson);
            textToCopy = JSON.stringify(parsedResource, null, 2);
          } else if (section === "resource_column_details") {
            const parsedResourceColumnDetails = JSON.parse(
              editedResourceColumnDetailsJson,
            );
            textToCopy = JSON.stringify(parsedResourceColumnDetails, null, 2);
          } else {
            // Fallback: copy both sections combined (for any global copy button)
            const parsedResource = JSON.parse(editedResourceJson);
            const parsedResourceColumnDetails = JSON.parse(
              editedResourceColumnDetailsJson,
            );
            textToCopy = JSON.stringify(
              {
                resource: parsedResource,
                resource_column_details: parsedResourceColumnDetails,
              },
              null,
              2,
            );
          }
        } catch {
          showToast(
            "Invalid JSON syntax. Please fix errors before copying.",
            "error",
          );
          return;
        }
      }
      await navigator.clipboard.writeText(textToCopy);
      showToast("JSON copied to clipboard!", "success");
    } catch (err) {
      console.error("Failed to copy JSON:", err);
    } finally {
      setIsCopying(false);
    }
  };

  const isEmptyObject = (value: any) =>
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0;

  const handleValidate = (section?: PayloadSection) => {
    if (!onValidate) return;
    let parsedPayload: any;

    if (isSplitMode) {
      let parsedResource: any;
      let parsedResourceColumnDetails: any;
      try {
        parsedResource = JSON.parse(editedResourceJson);
      } catch {
        showToast(
          "Invalid JSON syntax in Resource JSON. Please fix errors before validating.",
          "error",
        );
        return;
      }
      try {
        parsedResourceColumnDetails = JSON.parse(
          editedResourceColumnDetailsJson,
        );
      } catch {
        showToast(
          "Invalid JSON syntax in Resource Column Details JSON. Please fix errors before validating.",
          "error",
        );
        return;
      }
      // Guard: both sections empty means there is nothing to validate.
      if (
        isEmptyObject(parsedResource) &&
        isEmptyObject(parsedResourceColumnDetails)
      ) {
        showToast(
          "Request body cannot be empty. Please provide at least one field before validating.",
          "warning",
        );
        return;
      }
      parsedPayload = {
        resource: parsedResource,
        resource_column_details: parsedResourceColumnDetails,
      };
    } else {
      try {
        parsedPayload = JSON.parse(editedPayload);
      } catch {
        showToast(
          "Invalid JSON syntax. Please fix errors before validating.",
          "error",
        );
        return;
      }
      // Guard: empty object {} has no fields to validate.
      // All-Optional Pydantic models accept {} silently on the backend, so we
      // catch this case here and give the user immediate feedback without a
      // round-trip.  Mirrors the guard added in request_validator_service.py.
      if (isEmptyObject(parsedPayload)) {
        showToast(
          "Request body cannot be empty. Please provide at least one field before validating.",
          "warning",
        );
        return;
      }
    }
    setActiveValidateSection(section ?? null);
    onValidate(parsedPayload, section);
  };

  // Clear the per-section "Validating..." indicator once the parent-driven
  // validation request completes.
  useEffect(() => {
    if (!isValidating) {
      setActiveValidateSection(null);
    }
  }, [isValidating]);

  return (
    <EnhancedQueryDialog
      fullWidth
      maxWidth="lg"
      open={openDialog}
      onClose={() => handleDialogClose && handleDialogClose()}
      className="main-dailog view-payload-dialog"
      hasUnsavedChanges={combinedIsModified}
      showConfirmationOnEsc={combinedIsModified}
    >
      <Box sx={{ padding: 3 }}>
        <Grid
          container
          className="dailog-header"
          justifyContent="space-between"
        >
          <label className="label-text pt-13">{dialogTitle}</label>
          <div
            className="close-icon"
            onClick={() => handleDialogClose && handleDialogClose()}
          ></div>
        </Grid>
        <Box
          className="dailog-body"
          sx={{
            height: validationResponse
              ? "calc(50vh - 100px)"
              : "calc(70vh - 150px)",
            minHeight: !validationResponse ? "250px" : "400px",
            maxHeight: !validationResponse ? "400px" : "600px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Validation Results Section */}
          {validationResponse && (
            <Box className="validation-results-section" sx={{ mb: 1 }}>
              {validationResponse.is_valid ? (
                <Box
                  className="validation-success"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: "#e8f5e9",
                    border: "1px solid #a5d6a7",
                  }}
                >
                  <CheckCircleOutlineIcon sx={{ color: "#2e7d32" }} />
                  <span style={{ color: "#2e7d32", fontWeight: 500 }}>
                    Payload is valid!
                  </span>
                </Box>
              ) : (
                <Box sx={{ maxHeight: "120px", overflowY: "auto" }}>
                  {validationResponse.errors &&
                    validationResponse.errors.length > 0 && (
                      <Box sx={{ mb: 1 }}>
                        {validationResponse.errors.map((error, index) => (
                          <Box
                            key={`error-${index}`}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              p: 1,
                              mb: 0.5,
                              borderRadius: 1,
                              bgcolor: "#ffebee",
                              border: "1px solid #ef9a9a",
                            }}
                          >
                            <ErrorOutlineIcon
                              sx={{ color: "#c62828", fontSize: 20, mt: "2px" }}
                            />
                            <span
                              style={{ color: "#c62828", fontSize: "14px" }}
                            >
                              {typeof error === "string"
                                ? error
                                : `${error.field || "unknown"}: ${
                                    error.error || "unknown error"
                                  }${error.type ? ` (${error.type})` : ""}`}
                            </span>
                          </Box>
                        ))}
                      </Box>
                    )}
                  {validationResponse.warnings &&
                    validationResponse.warnings.length > 0 && (
                      <Box>
                        {validationResponse.warnings.map((warning, index) => (
                          <Box
                            key={`warning-${index}`}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                              p: 1,
                              mb: 0.5,
                              borderRadius: 1,
                              bgcolor: "#fff3e0",
                              border: "1px solid #ffcc80",
                            }}
                          >
                            <WarningAmberIcon
                              sx={{ color: "#e65100", fontSize: 20, mt: "2px" }}
                            />
                            <span
                              style={{ color: "#e65100", fontSize: "14px" }}
                            >
                              {typeof warning === "string"
                                ? warning
                                : `${warning.field || "unknown"}: ${
                                    warning.error || "unknown warning"
                                  }${warning.type ? ` (${warning.type})` : ""}`}
                            </span>
                          </Box>
                        ))}
                      </Box>
                    )}
                </Box>
              )}
            </Box>
          )}
          {isSplitMode ? (
            <Box
              className="accordion-panel payload-accordion-panel"
              sx={{ flex: 1, overflowY: "auto" }}
            >
              <Accordion
                className="heading-bold"
                elevation={0}
                square
                disableGutters
                expanded={resourceExpanded}
                onChange={() => setResourceExpanded((prev) => !prev)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="resource-json-content"
                  id="resource-json-header"
                >
                  <div className="custom-actions">
                    <div className="heading">Resource JSON</div>
                    <div className="action-btns">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetJson();
                        }}
                        className="btn-orange"
                        disabled={!combinedIsModified}
                        startIcon={<RestartAltIcon />}
                        sx={{ marginRight: "8px" }}
                      >
                        Reset
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyJson("resource");
                        }}
                        className="btn-orange"
                        disabled={isCopying}
                        startIcon={<ContentCopyIcon />}
                        sx={{ marginRight: "8px" }}
                      >
                        {isCopying ? "Copying..." : "Copy JSON"}
                      </Button>
                      {onValidate && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleValidate("resource");
                          }}
                          className="btn-orange"
                          disabled={isValidating}
                          startIcon={
                            isValidating &&
                            activeValidateSection === "resource" ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : undefined
                          }
                        >
                          {isValidating && activeValidateSection === "resource"
                            ? "Validating..."
                            : "Validate"}
                        </Button>
                      )}
                    </div>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <Box className="editor-parent accordion-editor-parent">
                    <AceEditor
                      mode="json"
                      theme="github"
                      value={editedResourceJson}
                      onChange={handleResourceEditorChange}
                      width="100%"
                      height="280px"
                      editorProps={{ $blockScrolling: true }}
                      showGutter
                      readOnly={false}
                      aria-label="resourcePayloadEditor"
                      name="resourcePayloadEditor"
                      fontSize={16}
                      showPrintMargin={false}
                      highlightActiveLine
                      setOptions={{
                        useWorker: false,
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: true,
                        enableSnippets: true,
                        showLineNumbers: true,
                        tabSize: 2,
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
              <Accordion
                className="heading-bold"
                elevation={0}
                square
                disableGutters
                expanded={resourceColumnDetailsExpanded}
                onChange={() =>
                  setResourceColumnDetailsExpanded((prev) => !prev)
                }
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="resource-column-details-json-content"
                  id="resource-column-details-json-header"
                >
                  <div className="custom-actions">
                    <div className="heading">
                      Resource Column Details JSON
                    </div>
                    <div className="action-btns">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetJson();
                        }}
                        className="btn-orange"
                        disabled={!combinedIsModified}
                        startIcon={<RestartAltIcon />}
                        sx={{ marginRight: "8px" }}
                      >
                        Reset
                      </Button>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyJson("resource_column_details");
                        }}
                        className="btn-orange"
                        disabled={isCopying}
                        startIcon={<ContentCopyIcon />}
                        sx={{ marginRight: "8px" }}
                      >
                        {isCopying ? "Copying..." : "Copy JSON"}
                      </Button>
                      {onValidate && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleValidate("resource_column_details");
                          }}
                          className="btn-orange"
                          disabled={isValidating}
                          startIcon={
                            isValidating &&
                            activeValidateSection ===
                              "resource_column_details" ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : undefined
                          }
                        >
                          {isValidating &&
                          activeValidateSection === "resource_column_details"
                            ? "Validating..."
                            : "Validate"}
                        </Button>
                      )}
                    </div>
                  </div>
                </AccordionSummary>
                <AccordionDetails>
                  <Box className="editor-parent accordion-editor-parent">
                    <AceEditor
                      mode="json"
                      theme="github"
                      value={editedResourceColumnDetailsJson}
                      onChange={handleResourceColumnDetailsEditorChange}
                      width="100%"
                      height="280px"
                      editorProps={{ $blockScrolling: true }}
                      showGutter
                      readOnly={false}
                      aria-label="resourceColumnDetailsPayloadEditor"
                      name="resourceColumnDetailsPayloadEditor"
                      fontSize={16}
                      showPrintMargin={false}
                      highlightActiveLine
                      setOptions={{
                        useWorker: false,
                        enableBasicAutocompletion: true,
                        enableLiveAutocompletion: true,
                        enableSnippets: true,
                        showLineNumbers: true,
                        tabSize: 2,
                      }}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          ) : (
            <Box className="editor-parent" sx={{ flex: 1, height: "100%" }}>
              <AceEditor
                mode="json"
                theme="github"
                value={editedPayload}
                onChange={handleEditorChange}
                width="100%"
                height="100%"
                editorProps={{ $blockScrolling: true }}
                showGutter
                readOnly={false}
                aria-label="payloadEditor"
                name="payloadEditor"
                fontSize={16}
                showPrintMargin={false}
                highlightActiveLine
                setOptions={{
                  useWorker: false,
                  enableBasicAutocompletion: true,
                  enableLiveAutocompletion: true,
                  enableSnippets: true,
                  showLineNumbers: true,
                  tabSize: 2,
                }}
              />
            </Box>
          )}
        </Box>

        <DialogActions className="dailog-footer">
          <Box className="footer-container">
            <Box className="footer-left-section">
              {!isSplitMode && (
                <>
                  <Button
                    onClick={handleResetJson}
                    className="btn-orange"
                    disabled={!combinedIsModified}
                    startIcon={<RestartAltIcon />}
                    sx={{ marginRight: "8px" }}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={() => handleCopyJson()}
                    className="btn-orange"
                    disabled={isCopying}
                    startIcon={<ContentCopyIcon />}
                    sx={{ marginRight: "8px" }}
                  >
                    {isCopying ? "Copying..." : "Copy JSON"}
                  </Button>
                </>
              )}
            </Box>
            <Box className="footer-right-section">
              {!isSplitMode && onValidate && (
                <Button
                  onClick={() => handleValidate()}
                  className="btn-orange"
                  disabled={isValidating}
                  startIcon={
                    isValidating ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                  sx={{ marginRight: "8px" }}
                >
                  {isValidating ? "Validating..." : "Validate"}
                </Button>
              )}
              <Button
                onClick={() => handleDialogClose && handleDialogClose()}
                className="btn-orange btn-dark"
              >
                Close
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Box>
    </EnhancedQueryDialog>
  );
};

export default ViewPayloadDialog;
